/** The workspace-channel event a landing reland reports on (BE constant GEOMETRY_CONSTRUCTION_EVENT). */
export const RELAND_EVENT = 'geometry_construction';

/** What the workspace channel reports when a re-landed well's landing analysis resolves. */
export interface RelandEvent {
  status?: 'completed' | 'failed';
  landingEditId?: number;
  uwi14?: string;
  fromFormation?: string | null;
  toFormation?: string | null;
  landingStatus?: 'pending' | 'landed' | 'unlanded' | 'failed';
  error?: string;
  /** The well landed in a formation this workspace has no zone for, so it left the inventory. */
  formationOutsideWorkspace?: boolean;
}

/** A toast as `toastAtom` takes it. */
export interface RelandToast {
  kind: 'success' | 'error' | 'info';
  title: string;
  subtitle?: string;
  timeout?: number;
}

/**
 * What a resolved landing means to the operator, as a toast.
 *
 * @param {RelandEvent} evt - The workspace-channel event the reland reported.
 * @return {RelandToast} The toast to show: a failure, an unlanded verdict, or where the well lands now.
 */
export const relandToast = (evt: RelandEvent): RelandToast => {
  const well = evt.uwi14 ? `Well ${evt.uwi14}` : 'The well';
  if (evt.status === 'failed' || evt.landingStatus === 'failed') {
    return {
      kind: 'error',
      title: 'Landing analysis failed',
      subtitle: evt.error ?? `${well} could not be re-landed.`,
    };
  }
  if (evt.formationOutsideWorkspace) {
    return {
      kind: 'info',
      title: 'Well left this inventory',
      subtitle: `${well} now lands in ${evt.toFormation}, which this workspace has no zone for, so it no longer has a slot here.`,
      timeout: 10000,
    };
  }
  if (evt.landingStatus === 'unlanded') {
    return {
      kind: 'success',
      title: 'Landing resolved',
      subtitle: `${well} lands in no formation at that depth; its slot has been regenerated.`,
    };
  }
  const moved =
    evt.toFormation &&
    evt.fromFormation &&
    evt.toFormation !== evt.fromFormation;
  return {
    kind: 'success',
    title: 'Landing resolved',
    subtitle: moved
      ? `${well} now lands in ${evt.toFormation} (was ${evt.fromFormation}); both formations were regenerated.`
      : `${well} still lands in ${
          evt.toFormation ?? evt.fromFormation ?? 'the same formation'
        }.`,
  };
};
